import { queryWithError } from "hybridspace-graphql/lib/utils/queryWithError";
import { addDays, subDays } from "owa-datetime";
import { logGreyError } from "owa-analytics";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";

import { GetPlacesScheduleDocument } from "../graphql/__generated__/GetPlacesSchedule.interface";
import { getGqlDateTime } from "../utils/getGqlDateTime";
import { getDevErrorPageState } from "places-diagnostics";
import { isAccountFeatureEnabled } from "owa-feature-flags";

import type { DateRange } from "owa-datetime-utils";
import type { GetPlacesScheduleQueryVariables } from "../graphql/__generated__/GetPlacesSchedule.interface";
import type { WorkLocationType, LocationIdType, LocationEntityType, LocationSourceType, } from "owa-graph-schema";
export default async function getScheduleWithLocation(smtpAddresses: string[], dateRange: DateRange): Promise<{
    __typename?: "HybridspaceScheduleInformation";
    scheduleId: string;
    error?: {
        __typename?: "CalendarDetailedError";
        message?: string | null;
        responseCode?: string | null;
        diagnosticData?: string | null;
    } | null;
    flexibleWorkingHours?: Array<{
        __typename?: "HybridspaceGetScheduleFlexibleWorkingHoursView";
        workLocationType: WorkLocationType;
        isConfirmed: boolean;
        start: {
            __typename?: "ExtendedDateTime";
            dateTime: string;
            timeZone: {
                __typename?: "TimeZoneWH";
                name: string;
            };
        };
        end: {
            __typename?: "ExtendedDateTime";
            dateTime: string;
            timeZone: {
                __typename?: "TimeZoneWH";
                name: string;
            };
        };
        workLocationDetails?: {
            __typename?: "Location";
            id?: string | null;
            idType?: LocationIdType | null;
            displayName?: string | null;
            annotation?: string | null;
            locationType?: LocationEntityType | null;
            locationUri?: string | null;
            locationSource?: LocationSourceType | null;
        } | null;
        building?: {
            __typename?: "Building";
            id?: string | null;
            name?: string | null;
            campusId?: string | null;
            address?: {
                __typename?: "PostalAddress";
                country?: string | null;
                postOfficeBox?: string | null;
                postalCode?: string | null;
                state?: string | null;
                city?: string | null;
                street?: string | null;
                type?: string | null;
            } | null;
        } | null;
    }> | null;
    flexibleWorkingHoursError?: {
        __typename?: "CalendarDetailedError";
        message?: string | null;
        responseCode?: string | null;
        diagnosticData?: string | null;
    } | null;
}[] | null | undefined> {
    if (smtpAddresses.length === 0) {
        return [];
    }
    
    let scheduleWithLocation;
    // Hoisted so the catch can attach structured diagnostics without logging any PII.
    const mailboxInfo = getModuleContextMailboxInfo();
    const viewType: "WorkPlansOnly" | "Default" = isAccountFeatureEnabled("cal-location-sharing-dropdown", mailboxInfo)
        ? "WorkPlansOnly"
        : "Default";
    let hasData = false;
    let hasGetScheduleWithLocation = false;
    try {
        // Manually throw an error to test error handling
        if (getDevErrorPageState() === "Schedule") {
            throw new Error("could not get schedule");
        }
        const scheduleVariables: GetPlacesScheduleQueryVariables | undefined = {
            input: {
                userId: mailboxInfo?.userIdentity ?? "",
                // The default interval in minutes.  Should not be less than 5 or greater than 1440.
                availabilityViewInterval: 30,
                startTime: getGqlDateTime(subDays(dateRange.start, 1)),
                endTime: getGqlDateTime(addDays(dateRange.end, 1)),
                schedules: [...smtpAddresses].sort(),
                mailboxInfo,
                viewType: viewType === "WorkPlansOnly" ? "WorkPlansOnly" : undefined,
            },
            // Explicit cast to ignore missing mailboxInfo due to schema mismatch
        } as GetPlacesScheduleQueryVariables;
        
        const { data } = await queryWithError(GetPlacesScheduleDocument, {
            variables: scheduleVariables,
        });
        
        hasData = !!data;
        hasGetScheduleWithLocation = !!data?.getScheduleWithLocation;
        
        if (data?.getScheduleWithLocation) {
            scheduleWithLocation = data.getScheduleWithLocation.schedules;
        }
        else {
            // HTTP 200 with no schedule payload (empty data / renamed field). queryWithError
            // does not treat this as an error, so nothing upstream captured it. Throw a
            // distinctly-messaged error so the catch below logs this branch.
            throw new Error("PlacesGetScheduleWithLocation empty data");
        }
    }
    catch (error) {
        // Log the ORIGINAL error here: this is the last point in the chain where it still
        // exists (getCollaboratorsWithLocation and PlacesExploreClient rewrap it into a
        // generic message, so PLACES_LOAD_SAVED_COLLABORATORS_LOCATIONS_FAILED otherwise
        // carries no real cause). Covers both the thrown-query-error branch (Apollo error
        // with graphQLErrors/networkError) and the empty-data branch above.
        // customData is structured, non-PII diagnostics only (no emails / userIdentity /
        // schedule ids / payload) so the failure branch and request shape are queryable.
        const failureBranch = error instanceof Error && error.message === "PlacesGetScheduleWithLocation empty data"
            ? "EmptyData"
            : "QueryOrApolloError";
        logGreyError("PlacesGetScheduleWithLocationFailed", mailboxInfo, error instanceof Error ? error : undefined, {
            failureBranch,
            operationName: "GetPlacesSchedule",
            smtpAddressCount: smtpAddresses.length,
            hasMailboxInfo: !!mailboxInfo,
            viewType,
            hasData,
            hasGetScheduleWithLocation,
        });
        throw new Error("PlacesGetScheduleWithLocation throw error");
    }
    return scheduleWithLocation;
}
